/**
 * Generic ratio / percentage maths — no domain knowledge, no entity types.
 *
 * These live in one place so every gauge, caption, KPI tile, CSV export and
 * agent answer computes a percentage the same way *and rounds it the same
 * way*. The rounding policy is the whole reason this is a library and not an
 * inline `toFixed(0)`: a completion or share figure is read by an operator to
 * decide whether work is finished, so the milestones — 0% and 100% — must mean
 * exactly what they say.
 */

/**
 * Divide, returning `null` (not `0`, not `NaN`, not `Infinity`) when the
 * denominator is missing, zero or negative.
 *
 * Why `null`: downstream a `0` reads as "0% done", which is a different
 * statement from "there was nothing to do". Callers that want a zero can
 * coalesce; callers that want to hide the figure can check for `null`.
 */
export function safeDivide(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): number | null {
  if (numerator == null || denominator == null) return null;
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) return null;
  if (denominator <= 0) return null;
  return numerator / denominator;
}

/**
 * `part / whole` as a 0–100 number, **unrounded**. `null` when `whole` is not a
 * positive finite number (see {@link safeDivide}).
 *
 * This is the one true definition of "what share / how complete" — a
 * completion percentage and a "share of today's work in this bucket" caption
 * are the same calculation and must not drift apart. Keep the raw value in
 * state and round only at display time with {@link formatPercent}.
 */
export function ratioPercent(
  part: number | null | undefined,
  whole: number | null | undefined,
): number | null {
  const ratio = safeDivide(part, whole);
  return ratio == null ? null : ratio * 100;
}

/**
 * True only when `part` reaches `whole` (and `whole` is a real positive count).
 * Use this to gate a "done" visual — a full green ring, a tick — rather than
 * testing a rounded percentage, which would light up at 99.6%.
 */
export function isComplete(
  part: number | null | undefined,
  whole: number | null | undefined,
): boolean {
  const ratio = safeDivide(part, whole);
  return ratio != null && ratio >= 1;
}

export interface FormatPercentOptions {
  /** Text for a `null` value (nothing to measure). Default `'—'`. */
  emptyText?: string;
}

/**
 * Whole-number percent for display, with the milestones guarded.
 *
 * - `null`            → `emptyText` (default `'—'`) — nothing to measure.
 * - exactly `>= 100`  → `'100%'`   — a real, earned 100.
 * - exactly `<= 0`    → `'0%'`     — a real zero.
 * - rounds up to 100  → `'99%'`    — e.g. 238/239 = 99.6%. Not done is not done.
 * - rounds down to 0  → `'<1%'`    — e.g. 1/500. Started is not "nothing".
 * - anything else      → nearest whole percent (95.65% → `'96%'`).
 *
 * Pass the raw value from {@link ratioPercent}; do not pre-round it, or the
 * milestone guard cannot tell 100.0 from 99.6.
 */
export function formatPercent(
  value: number | null,
  options: FormatPercentOptions = {},
): string {
  const { emptyText = '—' } = options;
  if (value == null || !Number.isFinite(value)) return emptyText;
  if (value >= 100) return '100%';
  if (value <= 0) return '0%';
  const rounded = Math.round(value);
  if (rounded >= 100) return '99%';
  if (rounded <= 0) return '<1%';
  return `${rounded}%`;
}

/**
 * One-decimal percent for display, with the same milestone guard as
 * {@link formatPercent}: only an exact 100 reads `100%`, only an exact 0 reads
 * `0%`.
 *
 * - rounds up to 100.0 (e.g. 99.96%) → `'99.9%'`. Not done is not done.
 * - rounds down to 0.0 (e.g. 0.04%)   → `'<0.1%'`. Started is not "nothing".
 * - anything else                      → nearest tenth (97.87% → `'97.9%'`).
 *
 * Pass the raw value from {@link ratioPercent}; do not pre-round it.
 */
export function formatPercentOneDecimal(
  value: number | null,
  options: FormatPercentOptions = {},
): string {
  // Same empty text as the whole-number formatter, so the two never drift.
  const emptyText = options.emptyText ?? formatPercent(null);
  if (value == null || !Number.isFinite(value)) return emptyText;
  if (value >= 100) return '100%';
  if (value <= 0) return '0%';
  const rounded = Math.round(value * 10) / 10;
  if (rounded >= 100) return '99.9%';
  if (rounded <= 0) return '<0.1%';
  return `${rounded.toFixed(1)}%`;
}

export interface PercentShareInput {
  key: string;
  count: number;
  /**
   * This slice must not display 100% unless it *is* the whole total. Use it for
   * a "fully done" slice so a headline never reads as complete while another
   * slice (partial, outstanding) is still non-zero.
   */
  neverFull?: boolean;
}

export interface PercentShare {
  key: string;
  count: number;
  /** Integer 0–100. Shares sum to exactly 100 when `total > 0`. */
  pct: number;
}

/**
 * Split a total into integer-percent slices that **sum to exactly 100**, for a
 * headline like "83% delivered · 9% partial · 8% outstanding".
 *
 * `parts` must partition `total` (counts add up to it). Method:
 *  1. Largest-remainder (Hamilton) rounding — floors, then hand the leftover
 *     points to the biggest fractional remainders.
 *  2. A non-empty slice never shows 0% — it rounds up to 1, taken from the
 *     current largest slice.
 *  3. A `neverFull` slice never shows 100% unless its count is the whole
 *     total — it caps at 99 and gives the freed point to the next-largest
 *     non-empty slice.
 *
 * With `total <= 0` every slice is 0.
 */
export function percentShares(parts: PercentShareInput[], total: number): PercentShare[] {
  if (!(total > 0)) return parts.map((p) => ({ key: p.key, count: p.count, pct: 0 }));

  const exact = new Map(parts.map((p) => [p.key, (p.count / total) * 100]));
  const pct = new Map(parts.map((p) => [p.key, Math.floor(exact.get(p.key) ?? 0)]));
  let remaining = 100 - [...pct.values()].reduce((s, n) => s + n, 0);
  const byRemainder = [...parts].sort(
    (a, b) => ((exact.get(b.key) ?? 0) % 1) - ((exact.get(a.key) ?? 0) % 1),
  );
  // Hand out the leftover points, biggest fractional remainder first. Loops
  // round-robin so the sum still reaches 100 even if `parts` slightly
  // under-covers `total` (it should partition it, but never trust that).
  for (let i = 0; remaining > 0 && byRemainder.length > 0; i += 1) {
    const p = byRemainder[i % byRemainder.length]!;
    pct.set(p.key, (pct.get(p.key) ?? 0) + 1);
    remaining -= 1;
  }

  const largestKey = (exclude?: string) =>
    [...pct.entries()]
      .filter(([k]) => k !== exclude)
      .sort((a, b) => b[1] - a[1])[0]?.[0];

  for (const p of parts) {
    if (p.count > 0 && (pct.get(p.key) ?? 0) === 0) {
      pct.set(p.key, 1);
      const donor = largestKey(p.key);
      if (donor) pct.set(donor, (pct.get(donor) ?? 0) - 1);
    }
  }

  for (const p of parts) {
    if (p.neverFull && (pct.get(p.key) ?? 0) >= 100 && p.count < total) {
      pct.set(p.key, 99);
      const other = parts
        .filter((o) => o.key !== p.key && o.count > 0)
        .sort((a, b) => (exact.get(b.key) ?? 0) - (exact.get(a.key) ?? 0))[0];
      if (other) pct.set(other.key, (pct.get(other.key) ?? 0) + 1);
    }
  }

  return parts.map((p) => ({ key: p.key, count: p.count, pct: pct.get(p.key) ?? 0 }));
}
